/**
 * useLatencyHistory Hook
 * Manages latency history in sessionStorage for sparkline visualization
 */

import { useState, useEffect, useCallback } from 'react';
import { LatencyHistory, LatencyHistoryEntry, HealthDependencies } from '../types';
import { 
  LATENCY_HISTORY_KEY, 
  MAX_HISTORY_GAP_MS, 
  MAX_HISTORY_ENTRIES 
} from '../constants';

interface UseLatencyHistoryReturn {
  history: LatencyHistoryEntry[];
  addEntry: (dependencies: HealthDependencies) => void;
}

const generateSessionId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const loadHistory = (): LatencyHistory | null => {
  try {
    const stored = sessionStorage.getItem(LATENCY_HISTORY_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

const saveHistory = (history: LatencyHistory): void => {
  try {
    sessionStorage.setItem(LATENCY_HISTORY_KEY, JSON.stringify(history));
  } catch {
    // Storage full or unavailable - silently fail
  }
};

export const useLatencyHistory = (): UseLatencyHistoryReturn => {
  const [history, setHistory] = useState<LatencyHistoryEntry[]>([]);

  // Load initial history on mount
  useEffect(() => {
    const stored = loadHistory();
    if (stored) {
      // Check if history is still continuous (not too old)
      const now = Date.now();
      if (now - stored.lastTimestamp <= MAX_HISTORY_GAP_MS) {
        setHistory(stored.entries);
      } else {
        // History is stale, clear it
        sessionStorage.removeItem(LATENCY_HISTORY_KEY);
      }
    }
  }, []);

  const addEntry = useCallback((dependencies: HealthDependencies) => {
    const now = Date.now();
    const stored = loadHistory();

    const newEntry: LatencyHistoryEntry = {
      timestamp: now,
      database: dependencies.database.latencyMs,
      cache: dependencies.cache.latencyMs,
      blobStorage: dependencies.blobStorage.latencyMs,
      serviceBus: dependencies.serviceBus.latencyMs
    };

    let updatedHistory: LatencyHistory;

    if (stored && (now - stored.lastTimestamp) <= MAX_HISTORY_GAP_MS) {
      // Continuous session - append entry
      const entries = [...stored.entries, newEntry].slice(-MAX_HISTORY_ENTRIES);
      updatedHistory = {
        sessionId: stored.sessionId,
        lastTimestamp: now,
        entries
      };
    } else {
      // New session or gap detected - start fresh
      updatedHistory = {
        sessionId: generateSessionId(),
        lastTimestamp: now,
        entries: [newEntry]
      };
    }

    saveHistory(updatedHistory);
    setHistory(updatedHistory.entries);
  }, []);

  return {
    history,
    addEntry
  };
};

export default useLatencyHistory;
